<script setup lang="ts">
import type { SupportAttachment } from '../../packages/client/src/types';
import { onBeforeUnmount, ref, watch } from 'vue';

/**
 * 消息里的视频：先显示文件名和大小，点了才经鉴权接口下载并播放，
 * 避免翻历史消息时自动下载几十 MB 的视频。播放地址是本地 blob，不暴露静态地址。
 */
const props = defineProps<{
  attachment: SupportAttachment;
  load: () => Promise<Blob>;
  /** 身份或会话变化时，清理播放地址并作废在途请求。 */
  scope: unknown;
}>();

const url = ref('');
const loading = ref(false);
const failed = ref(false);
let generation = 0;

function release(): void {
  if (url.value) URL.revokeObjectURL(url.value);
  url.value = '';
}

function sizeText(size: number | string): string {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

async function play(): Promise<void> {
  if (loading.value || url.value) return;
  const gate = ++generation;
  loading.value = true;
  failed.value = false;
  try {
    const blob = await props.load();
    if (gate !== generation) return;
    url.value = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: props.attachment.mime }));
  } catch {
    if (gate === generation) failed.value = true;
  } finally {
    if (gate === generation) loading.value = false;
  }
}

watch(() => [props.scope, props.attachment.id], () => {
  generation += 1;
  release();
  loading.value = false;
  failed.value = false;
}, { flush: 'sync' });

onBeforeUnmount(() => {
  generation += 1;
  release();
});
</script>

<template>
  <div class="message-video" data-testid="message-video">
    <video v-if="url" :src="url" autoplay class="message-video__player" controls playsinline preload="metadata" data-testid="message-video-player"></video>
    <button v-else class="message-video__cover" :disabled="loading" data-testid="message-video-play" type="button" @click.stop="play">
      <span class="message-video__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5-11-6.5Z" /></svg>
      </span>
      <span class="message-video__text">
        <strong>{{ loading ? '视频加载中…' : failed ? '视频加载失败，点击重试' : '点击播放视频' }}</strong>
        <small>{{ attachment.name }}<template v-if="sizeText(attachment.size)"> · {{ sizeText(attachment.size) }}</template></small>
      </span>
    </button>
  </div>
</template>

<style scoped>
.message-video { width: 280px; max-width: 100%; margin: 4px 0; }
.message-video__player { display: block; width: 100%; max-height: 360px; border-radius: 8px; background: #000; }
.message-video__cover {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 120px;
  padding: 12px 14px;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #1f2937, #374151);
  color: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.message-video__cover:disabled { cursor: wait; }
.message-video__icon { display: grid; flex: none; place-items: center; width: 40px; height: 40px; border-radius: 50%; background: rgb(255 255 255 / 20%); }
.message-video__icon svg { width: 20px; height: 20px; margin-left: 2px; fill: #fff; }
.message-video__text { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.message-video__text strong { font-size: 13px; font-weight: 600; }
.message-video__text small { overflow: hidden; color: rgb(255 255 255 / 70%); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
</style>
